import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2, Database, Users, FileText, Package, ShieldCheck } from "lucide-react";

interface TestResult {
  name: string;
  status: "pass" | "fail" | "running" | "pending";
  detail?: string;
}

export default function SystemTestPage() {
  const { user, isAuthenticated } = useAuth();
  const [results, setResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);

  const updateResult = (name: string, status: TestResult["status"], detail?: string) => {
    setResults((prev) => {
      const idx = prev.findIndex((r) => r.name === name);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { name, status, detail };
        return copy;
      }
      return [...prev, { name, status, detail }];
    });
  };

  const runTests = async () => {
    setRunning(true);
    setResults([]);

    const tests: { name: string; fn: () => Promise<string> }[] = [
      {
        name: "Database Connection",
        fn: async () => {
          const { data, error } = await supabase.from("profiles").select("id").limit(1);
          if (error) throw error;
          return `Connected successfully. Query returned ${data?.length ?? 0} row(s).`;
        },
      },
      {
        name: "Authentication Active",
        fn: async () => {
          if (!isAuthenticated || !user) throw new Error("Not authenticated");
          return `Logged in as ${user.email} (role: ${user.role || "user"})`;
        },
      },
      {
        name: "Profiles Table Read",
        fn: async () => {
          const { data, error } = await supabase.from("profiles").select("id, email, role").eq("id", user!.id).maybeSingle();
          if (error) throw error;
          if (!data) throw new Error("Profile not found");
          return `Profile found: ${data.email} (${data.role})`;
        },
      },
      {
        name: "Companies Table Read",
        fn: async () => {
          const { count, error } = await supabase.from("companies").select("*", { count: "exact", head: true });
          if (error) throw error;
          return `${count ?? 0} companies in database`;
        },
      },
      {
        name: "Ledgers Table CRUD",
        fn: async () => {
          // Insert
          const { data: row, error: insertErr } = await supabase.from("ledgers").insert({
            name: "__TEST_LEDGER__",
            group: "Current Assets",
            nature: "Asset",
            created_by: user!.id,
          }).select().single();
          if (insertErr) throw insertErr;

          // Read
          const { data: readRow } = await supabase.from("ledgers").select("*").eq("id", row.id).single();
          if (!readRow) throw new Error("Read failed");

          // Delete
          await supabase.from("ledgers").delete().eq("id", row.id);
          return "Insert → Read → Delete: All passed";
        },
      },
      {
        name: "Vouchers Table CRUD",
        fn: async () => {
          const { data: row, error } = await supabase.from("vouchers").insert({
            voucher_number: "__TEST__",
            type: "Journal",
            narration: "System test",
            created_by: user!.id,
          }).select().single();
          if (error) throw error;
          await supabase.from("vouchers").delete().eq("id", row.id);
          return "Insert → Delete: All passed";
        },
      },
      {
        name: "Customers Table CRUD",
        fn: async () => {
          const { data: row, error } = await supabase.from("customers").insert({
            name: "__TEST_CUSTOMER__",
            type: "Customer",
            created_by: user!.id,
          }).select().single();
          if (error) throw error;
          await supabase.from("customers").delete().eq("id", row.id);
          return "Insert → Delete: All passed";
        },
      },
      {
        name: "Inventory Items Table CRUD",
        fn: async () => {
          const { data: row, error } = await supabase.from("inventory_items").insert({
            name: "__TEST_ITEM__",
            created_by: user!.id,
          }).select().single();
          if (error) throw error;
          await supabase.from("inventory_items").delete().eq("id", row.id);
          return "Insert → Delete: All passed";
        },
      },
      {
        name: "RLS Policies Active",
        fn: async () => {
          // Try reading another user's data - should return empty
          const { data } = await supabase.from("ledgers").select("id").eq("created_by", "00000000-0000-0000-0000-000000000000");
          return `RLS working. Query for non-existent user returned ${data?.length ?? 0} rows.`;
        },
      },
    ];

    for (const test of tests) {
      updateResult(test.name, "running");
      try {
        const detail = await test.fn();
        updateResult(test.name, "pass", detail);
      } catch (err: any) {
        updateResult(test.name, "fail", err.message || "Unknown error");
      }
    }

    setRunning(false);
  };

  const passed = results.filter((r) => r.status === "pass").length;
  const failed = results.filter((r) => r.status === "fail").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">System Test Panel</h1>
          <p className="text-sm text-muted-foreground">Verify database connectivity and CRUD operations</p>
        </div>
        <Button onClick={runTests} disabled={running} className="gap-2">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
          {running ? "Running Tests..." : "Run All Tests"}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="flex gap-4">
          <Card className="flex-1">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <div>
                <p className="text-2xl font-bold text-success">{passed}</p>
                <p className="text-xs text-muted-foreground">Passed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="flex-1">
            <CardContent className="p-4 flex items-center gap-3">
              <XCircle className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-2xl font-bold text-destructive">{failed}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-3">
        {results.map((r) => (
          <Card key={r.name} className={`border ${r.status === "pass" ? "border-success/30" : r.status === "fail" ? "border-destructive/30" : "border-border"}`}>
            <CardContent className="p-4 flex items-center gap-3">
              {r.status === "running" && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
              {r.status === "pass" && <CheckCircle2 className="h-5 w-5 text-success" />}
              {r.status === "fail" && <XCircle className="h-5 w-5 text-destructive" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{r.name}</p>
                {r.detail && <p className="text-xs text-muted-foreground truncate">{r.detail}</p>}
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                r.status === "pass" ? "bg-success/10 text-success" :
                r.status === "fail" ? "bg-destructive/10 text-destructive" :
                "bg-muted text-muted-foreground"
              }`}>
                {r.status.toUpperCase()}
              </span>
            </CardContent>
          </Card>
        ))}

        {results.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Click "Run All Tests" to verify database connectivity and CRUD operations.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
